import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import InventoryAnalytics from '@/components/InventoryAnalytics';
import InventoryAgeing from '@/pages/InventoryAgeing';
import { 
  Package, 
  TrendingUp, 
  AlertTriangle, 
  XCircle, 
  RefreshCw, 
  ArrowRight,
  BarChart3,
  IndianRupee,
  Boxes,
  Download,
  Loader2,
  Activity,
  Layers
} from 'lucide-react';
import { CsvDownloadButton } from '@/components/CsvDownloadButton';

var API = process.env.REACT_APP_BACKEND_URL + '/api';

function InventoryDashboard() {
  var loadingState = React.useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  
  var dashboardState = React.useState(null);
  var dashboard = dashboardState[0];
  var setDashboard = dashboardState[1];
  
  var exportingState = React.useState(false);
  var exporting = exportingState[0];
  var setExporting = exportingState[1];

  // Phase-3: batch-level summary (products with multi-MRP, total active batches, etc.)
  var batchStatsState = React.useState(null);
  var batchStats = batchStatsState[0];
  var setBatchStats = batchStatsState[1];

  var tabState = React.useState('overview');
  var activeTab = tabState[0];
  var setActiveTab = tabState[1];

  React.useEffect(function() {
    fetchDashboard();
    fetchBatchStats();
  }, []);

  function fetchDashboard() {
    setLoading(true);
    var token = localStorage.getItem('token');
    axios.get(API + '/inventory/dashboard', {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(response) {
      setDashboard(response.data);
      setLoading(false);
    }).catch(function(error) {
      toast.error('Failed to fetch inventory dashboard');
      setLoading(false);
    });
  }

  function fetchBatchStats() {
    var token = localStorage.getItem('token');
    axios.get(API + '/inventory/batches?only_available=true', {
      headers: { Authorization: 'Bearer ' + token }
    }).then(function(response) {
      var batches = response.data || [];
      var productIds = {};
      var productCount = {};
      batches.forEach(function(b) {
        productIds[b.product_id] = true;
        productCount[b.product_id] = (productCount[b.product_id] || 0) + 1;
      });
      var multiMrpCount = 0;
      Object.keys(productCount).forEach(function(pid) {
        if (productCount[pid] > 1) multiMrpCount += 1;
      });
      setBatchStats({
        total_active_batches: batches.length,
        products_with_batches: Object.keys(productIds).length,
        multi_mrp_products: multiMrpCount,
      });
    }).catch(function() { /* Silent — dashboard shows "—" for batch tile */ });
  }

  function exportValuation(groupBy) {
    setExporting(true);
    var token = localStorage.getItem('token');
    axios.post(API + '/inventory/export/valuation?group_by=' + groupBy, {}, {
      headers: { Authorization: 'Bearer ' + token },
      responseType: 'blob'
    }).then(function(response) {
      var url = window.URL.createObjectURL(new Blob([response.data]));
      var link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Stock_Valuation_' + groupBy + '_' + new Date().toISOString().slice(0,10) + '.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Valuation report exported');
      setExporting(false);
    }).catch(function(error) {
      toast.error('Failed to export');
      setExporting(false);
    });
  }

  function formatCurrency(value) {
    return '₹' + (value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
  }

  if (loading) {
    return React.createElement('div', { className: 'flex items-center justify-center h-64' },
      React.createElement(Loader2, { className: 'w-8 h-8 animate-spin text-blue-600' })
    );
  }

  if (!dashboard) {
    return React.createElement('div', { className: 'text-center py-12 text-slate-500' }, 'Failed to load dashboard');
  }

  var summary = dashboard.summary || {};

  return React.createElement('div', { className: 'space-y-6', 'data-testid': 'inventory-dashboard' },
    // Header
    React.createElement('div', { className: 'flex items-center justify-between flex-wrap gap-4' },
      React.createElement('div', null,
        React.createElement('h1', { className: 'text-3xl font-heading font-bold text-primary' }, 'Inventory Management'),
        React.createElement('p', { className: 'text-slate-600 mt-1' }, 'Real-time stock tracking and valuation (FIFO)')
      ),
      React.createElement('div', { className: 'flex gap-2 flex-wrap' },
        React.createElement(Button, { variant: 'outline', onClick: function() { exportValuation('category'); }, disabled: exporting, className: 'gap-2' },
          exporting ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' }) : React.createElement(Download, { className: 'w-4 h-4' }),
          'Export by Category'
        ),
        React.createElement(Button, { variant: 'outline', onClick: function() { exportValuation('brand'); }, disabled: exporting, className: 'gap-2' },
          exporting ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' }) : React.createElement(Download, { className: 'w-4 h-4' }),
          'Export by Brand'
        ),
        React.createElement(CsvDownloadButton, { onDownload: function() { exportValuation('category'); }, testId: 'valuation-category-csv', label: 'Category CSV', size: 'default', disabled: exporting }),
        React.createElement(CsvDownloadButton, { onDownload: function() { exportValuation('brand'); }, testId: 'valuation-brand-csv', label: 'Brand CSV', size: 'default', disabled: exporting }),
        React.createElement(Button, { onClick: fetchDashboard, variant: 'outline', className: 'gap-2' },
          React.createElement(RefreshCw, { className: 'w-4 h-4' }),
          'Refresh'
        )
      )
    ),

    // Tabs for Overview and Analytics
    React.createElement(Tabs, { value: activeTab, onValueChange: setActiveTab, className: 'w-full' },
      React.createElement(TabsList, { className: 'grid w-full max-w-lg grid-cols-3' },
        React.createElement(TabsTrigger, { value: 'overview', className: 'gap-2', 'data-testid': 'tab-overview' },
          React.createElement(Boxes, { className: 'w-4 h-4' }),
          'Overview'
        ),
        React.createElement(TabsTrigger, { value: 'ageing', className: 'gap-2', 'data-testid': 'tab-ageing' },
          React.createElement(AlertTriangle, { className: 'w-4 h-4' }),
          'Ageing'
        ),
        React.createElement(TabsTrigger, { value: 'analytics', className: 'gap-2', 'data-testid': 'tab-analytics' },
          React.createElement(Activity, { className: 'w-4 h-4' }),
          'Analytics'
        )
      ),

      // Overview Tab Content
      React.createElement(TabsContent, { value: 'overview', className: 'space-y-6 mt-6' },
        // Summary Cards Row 1
        React.createElement('div', { className: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4' },
          // Total SKUs
          React.createElement(Card, { className: 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200' },
            React.createElement(CardContent, { className: 'p-6' },
              React.createElement('div', { className: 'flex items-center justify-between' },
                React.createElement('div', null,
                  React.createElement('p', { className: 'text-sm text-blue-600 font-medium' }, 'Total SKUs'),
                  React.createElement('p', { className: 'text-3xl font-bold text-blue-800', 'data-testid': 'total-skus' }, summary.total_skus || 0),
                  React.createElement('p', { className: 'text-xs text-blue-600 mt-1' }, (summary.skus_in_stock || 0) + ' in stock')
                ),
                React.createElement(Package, { className: 'w-12 h-12 text-blue-500 opacity-80' })
              )
            )
          ),
          
          // Total Stock Qty
          React.createElement(Card, { className: 'bg-gradient-to-br from-green-50 to-green-100 border-green-200' },
            React.createElement(CardContent, { className: 'p-6' },
              React.createElement('div', { className: 'flex items-center justify-between' },
                React.createElement('div', null,
                  React.createElement('p', { className: 'text-sm text-green-600 font-medium' }, 'Total Stock Qty'),
                  React.createElement('p', { className: 'text-3xl font-bold text-green-800', 'data-testid': 'total-stock-qty' }, (summary.total_stock_qty || 0).toLocaleString()),
                  React.createElement('p', { className: 'text-xs text-green-600 mt-1' }, 'Units in warehouse')
                ),
                React.createElement(Boxes, { className: 'w-12 h-12 text-green-500 opacity-80' })
              )
            )
          ),
          
          // Stock Value
          React.createElement(Card, { className: 'bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200' },
            React.createElement(CardContent, { className: 'p-6' },
              React.createElement('div', { className: 'flex items-center justify-between' },
                React.createElement('div', null,
                  React.createElement('p', { className: 'text-sm text-purple-600 font-medium' }, 'Stock Value (Excl GST)'),
                  React.createElement('p', { className: 'text-3xl font-bold text-purple-800', 'data-testid': 'stock-value' }, formatCurrency(summary.total_value_excl_gst)),
                  React.createElement('p', { className: 'text-xs text-purple-600 mt-1' }, 'Incl GST: ' + formatCurrency(summary.total_value_incl_gst))
                ),
                React.createElement(IndianRupee, { className: 'w-12 h-12 text-purple-500 opacity-80' })
              )
            )
          ),

          // Allocated Value — reservations against active orders (June 2026 spec)
          React.createElement(Card, { className: 'bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200' },
            React.createElement(CardContent, { className: 'p-6' },
              React.createElement('div', { className: 'flex items-center justify-between' },
                React.createElement('div', null,
                  React.createElement('p', { className: 'text-sm text-amber-700 font-medium' }, 'Allocated Value'),
                  React.createElement('p', { className: 'text-3xl font-bold text-amber-800', 'data-testid': 'allocated-value' }, formatCurrency(summary.total_allocated_value || 0)),
                  React.createElement('p', { className: 'text-xs text-amber-700 mt-1' }, (summary.total_allocated_qty || 0).toLocaleString() + ' units reserved')
                ),
                React.createElement(Boxes, { className: 'w-12 h-12 text-amber-500 opacity-80' })
              )
            )
          ),

          // Available Value — free-to-sell value (June 2026 spec)
          React.createElement(Card, { className: 'bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200' },
            React.createElement(CardContent, { className: 'p-6' },
              React.createElement('div', { className: 'flex items-center justify-between' },
                React.createElement('div', null,
                  React.createElement('p', { className: 'text-sm text-teal-700 font-medium' }, 'Available Value'),
                  React.createElement('p', { className: 'text-3xl font-bold text-teal-800', 'data-testid': 'available-value' }, formatCurrency(summary.total_available_value || 0)),
                  React.createElement('p', { className: 'text-xs text-teal-700 mt-1' }, (summary.total_available_qty || 0).toLocaleString() + ' units free')
                ),
                React.createElement(Boxes, { className: 'w-12 h-12 text-teal-500 opacity-80' })
              )
            )
          ),
          
          // Potential Margin (Outward Selling − Inward Cost)
          React.createElement(Card, { className: 'bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200' },
            React.createElement(CardContent, { className: 'p-6' },
              React.createElement('div', { className: 'flex items-center justify-between' },
                React.createElement('div', null,
                  React.createElement('p', { className: 'text-sm text-emerald-600 font-medium' }, 'Potential Margin'),
                  React.createElement('p', { className: 'text-3xl font-bold text-emerald-800', 'data-testid': 'potential-margin' }, formatCurrency(summary.potential_margin)),
                  React.createElement('p', { className: 'text-xs text-emerald-600 mt-1' },
                    'Margin: ',
                    React.createElement('strong', null, (summary.potential_margin_pct !== undefined && summary.potential_margin_pct !== null) ? summary.potential_margin_pct + '%' : 'N/A'),
                    ' · Sell: ',
                    formatCurrency(summary.total_selling_value || 0),
                    ' − Cost: ',
                    formatCurrency(summary.total_cost_value || 0))
                ),
                React.createElement(TrendingUp, { className: 'w-12 h-12 text-emerald-500 opacity-80' })
              )
            )
          ),

          // Phase-3: Multi-MRP Batches tile
          React.createElement(Card, { className: 'bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200 cursor-pointer hover:shadow-md transition-shadow', onClick: function() { window.location.href = '/inventory/batches'; }, 'data-testid': 'batch-tile' },
            React.createElement(CardContent, { className: 'p-6' },
              React.createElement('div', { className: 'flex items-center justify-between' },
                React.createElement('div', null,
                  React.createElement('p', { className: 'text-sm text-indigo-600 font-medium' }, 'Active Batches'),
                  React.createElement('p', { className: 'text-3xl font-bold text-indigo-800', 'data-testid': 'batch-tile-count' },
                    batchStats ? batchStats.total_active_batches : '—'),
                  React.createElement('p', { className: 'text-xs text-indigo-600 mt-1' },
                    batchStats
                      ? React.createElement(React.Fragment, null,
                          'Across ',
                          React.createElement('strong', null, batchStats.products_with_batches),
                          ' products · ',
                          React.createElement('strong', null, batchStats.multi_mrp_products),
                          ' on multi-MRP')
                      : 'Loading…')
                ),
                React.createElement(Layers, { className: 'w-12 h-12 text-indigo-500 opacity-80' })
              )
            )
          )
        ),

        // Alerts Row
        React.createElement('div', { className: 'grid grid-cols-1 md:grid-cols-2 gap-4' },
          // Low Stock Alert
          React.createElement(Card, { className: 'border-amber-200 bg-amber-50' },
            React.createElement(CardContent, { className: 'p-4' },
              React.createElement('div', { className: 'flex items-center justify-between' },
                React.createElement('div', { className: 'flex items-center gap-3' },
                  React.createElement(AlertTriangle, { className: 'w-8 h-8 text-amber-600' }),
                  React.createElement('div', null,
                    React.createElement('p', { className: 'font-semibold text-amber-800' }, 'Low Stock Items'),
                    React.createElement('p', { className: 'text-2xl font-bold text-amber-900', 'data-testid': 'low-stock-count' }, summary.low_stock_count || 0)
                  )
                ),
                React.createElement(Button, { 
                  variant: 'outline', 
                  size: 'sm',
                  className: 'border-amber-300 hover:bg-amber-100',
                  onClick: function() { window.location.href = '/inventory/low-stock'; },
                  'data-testid': 'view-low-stock-btn'
                }, 'View Report ', React.createElement(ArrowRight, { className: 'w-4 h-4 ml-1' }))
              )
            )
          ),
          
          // Out of Stock Alert
          React.createElement(Card, { className: 'border-red-200 bg-red-50' },
            React.createElement(CardContent, { className: 'p-4' },
              React.createElement('div', { className: 'flex items-center justify-between' },
                React.createElement('div', { className: 'flex items-center gap-3' },
                  React.createElement(XCircle, { className: 'w-8 h-8 text-red-600' }),
                  React.createElement('div', null,
                    React.createElement('p', { className: 'font-semibold text-red-800' }, 'Out of Stock Items'),
                    React.createElement('p', { className: 'text-2xl font-bold text-red-900', 'data-testid': 'out-of-stock-count' }, summary.out_of_stock_count || 0)
                  )
                ),
                React.createElement(Button, { 
                  variant: 'outline', 
                  size: 'sm',
                  className: 'border-red-300 hover:bg-red-100',
                  onClick: function() { window.location.href = '/inventory/stock-list?status=out_of_stock'; },
                  'data-testid': 'view-out-of-stock-btn'
                }, 'View Items ', React.createElement(ArrowRight, { className: 'w-4 h-4 ml-1' }))
              )
            )
          )
        ),

        // Category and Brand Breakdown
        React.createElement('div', { className: 'grid grid-cols-1 lg:grid-cols-2 gap-6' },
          // Category Breakdown
          React.createElement(Card, null,
            React.createElement(CardHeader, null,
              React.createElement(CardTitle, { className: 'flex items-center gap-2' },
                React.createElement(BarChart3, { className: 'w-5 h-5' }),
                'Stock Value by Category'
              )
            ),
            React.createElement(CardContent, null,
              React.createElement('div', { className: 'space-y-3' },
                (dashboard.category_breakdown || []).slice(0, 8).map(function(cat, idx) {
                  var maxVal = Math.max.apply(null, dashboard.category_breakdown.map(function(c) { return c.value; }));
                  var pct = maxVal > 0 ? (cat.value / maxVal * 100) : 0;
                  return React.createElement('div', { key: idx, className: 'space-y-1' },
                    React.createElement('div', { className: 'flex justify-between text-sm' },
                      React.createElement('span', { className: 'font-medium' }, cat.category),
                      React.createElement('span', { className: 'text-slate-600' }, formatCurrency(cat.value), ' (', cat.sku_count, ' SKUs)')
                    ),
                    React.createElement('div', { className: 'h-2 bg-slate-100 rounded-full overflow-hidden' },
                      React.createElement('div', { 
                        className: 'h-full bg-blue-500 rounded-full transition-all',
                        style: { width: pct + '%' }
                      })
                    )
                  );
                })
              )
            )
          ),
          
          // Brand Breakdown
          React.createElement(Card, null,
            React.createElement(CardHeader, null,
              React.createElement(CardTitle, { className: 'flex items-center gap-2' },
                React.createElement(BarChart3, { className: 'w-5 h-5' }),
                'Stock Value by Brand'
              )
            ),
            React.createElement(CardContent, null,
              React.createElement('div', { className: 'space-y-3' },
                (dashboard.brand_breakdown || []).slice(0, 8).map(function(brand, idx) {
                  var maxVal = Math.max.apply(null, dashboard.brand_breakdown.map(function(b) { return b.value; }));
                  var pct = maxVal > 0 ? (brand.value / maxVal * 100) : 0;
                  return React.createElement('div', { key: idx, className: 'space-y-1' },
                    React.createElement('div', { className: 'flex justify-between text-sm' },
                      React.createElement('span', { className: 'font-medium' }, brand.brand),
                      React.createElement('span', { className: 'text-slate-600' }, formatCurrency(brand.value), ' (', brand.qty, ' units)')
                    ),
                    React.createElement('div', { className: 'h-2 bg-slate-100 rounded-full overflow-hidden' },
                      React.createElement('div', { 
                        className: 'h-full bg-green-500 rounded-full transition-all',
                        style: { width: pct + '%' }
                      })
                    )
                  );
                })
              )
            )
          )
        ),

        // Recent Updates
        React.createElement(Card, null,
          React.createElement(CardHeader, null,
            React.createElement(CardTitle, null, 'Recent Stock Updates (Last GRNs)')
          ),
          React.createElement(CardContent, null,
            (dashboard.recent_updates || []).length === 0 
              ? React.createElement('p', { className: 'text-slate-500 text-center py-4' }, 'No recent updates')
              : React.createElement('div', { className: 'space-y-2' },
                  (dashboard.recent_updates || []).map(function(update, idx) {
                    return React.createElement('div', { 
                      key: idx, 
                      className: 'flex items-center justify-between p-3 bg-slate-50 rounded-lg'
                    },
                      React.createElement('div', null,
                        React.createElement('p', { className: 'font-medium' }, update.product_name),
                        React.createElement('p', { className: 'text-sm text-slate-600' }, 
                          update.grn_number, ' • ', update.supplier_name
                        )
                      ),
                      React.createElement('div', { className: 'text-right' },
                        React.createElement(Badge, { className: 'bg-green-100 text-green-800' }, 
                          '+', update.quantity_added, ' units'
                        ),
                        React.createElement('p', { className: 'text-xs text-slate-500 mt-1' }, update.grn_date)
                      )
                    );
                  })
                )
          )
        )
      ),

      // Analytics Tab Content
      React.createElement(TabsContent, { value: 'analytics', className: 'mt-6' },
        React.createElement(InventoryAnalytics, null)
      ),
      
      // Ageing Tab Content
      React.createElement(TabsContent, { value: 'ageing', className: 'mt-6' },
        React.createElement(InventoryAgeing, null)
      )
    )
  );
}

export default InventoryDashboard;
